import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface RouteState {
  path: string;
  query: URLSearchParams;
}

interface NavigationContextValue extends RouteState {
  navigate: (target: string) => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

const DEFAULT_PATH = '/';

function parseHash(): RouteState {
  if (typeof window === 'undefined') {
    return { path: DEFAULT_PATH, query: new URLSearchParams() };
  }
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (!hash) {
    return { path: DEFAULT_PATH, query: new URLSearchParams() };
  }
  const [path, queryString] = hash.split('?');
  return {
    path: path || DEFAULT_PATH,
    query: new URLSearchParams(queryString ?? ''),
  };
}

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const [route, setRoute] = useState<RouteState>(() => parseHash());

  useEffect(() => {
    const handler = () => setRoute(parseHash());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  const navigate = useCallback((target: string) => {
    const normalized = target.startsWith('#') ? target : `#${target}`;
    if (normalized === window.location.hash) return;
    window.location.hash = normalized;
  }, []);

  const value = useMemo<NavigationContextValue>(
    () => ({ ...route, navigate }),
    [navigate, route]
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigation = (): NavigationContextValue => {
  const value = useContext(NavigationContext);
  if (!value) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return value;
};
